// client/src/components/shared/PageHeader.js — one header for the main portal pages
// (docs/ui-tidy.md): title, a one-line description, the primary action on the right,
// and secondary actions. On phones the secondary actions fold into a "More" menu; on
// wider screens they sit beside the primary action. <AboutThisPage> holds the longer
// helper text, closed by default, so the page itself starts with the work.
// Presentation only: the actions passed in are the page's own buttons, unchanged.
import React, { useEffect, useRef, useState } from 'react';
import { useIsPhone } from './CollapsibleCard';

const MoreMenu = ({ children }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
    }, [open]);
    return (
        <div className="relative" ref={ref}>
            <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="true" aria-expanded={open}
                className="px-3 py-2 min-h-[40px] text-sm font-medium bg-white border border-slate-300 rounded-md hover:bg-slate-50">
                More ▾
            </button>
            {/* Kept mounted while closed so a page's own controls (e.g. a filter) keep their state. */}
            <div className={`${open ? '' : 'hidden '}absolute right-0 z-20 mt-1 w-64 max-w-[85vw] rounded-md border border-slate-200 bg-white p-2 shadow-lg flex flex-col items-stretch gap-2`}>
                {children}
            </div>
        </div>
    );
};

/**
 * @param {string} title
 * @param {node} [description] one line under the title
 * @param {node} [actions] the primary action(s), always visible on the right
 * @param {node} [secondary] other actions; a "More" menu on phones
 * @param {node} [children] extra rows under the header (status notes, tabs)
 */
export default function PageHeader({ title, description, actions, secondary, children, className = '' }) {
    const phone = useIsPhone();
    return (
        <div className={`mb-5 ${className}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
                    {description && <p className="mt-1 text-sm text-slate-600 max-w-3xl">{description}</p>}
                </div>
                {(actions || secondary) && (
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        {secondary && (phone ? <MoreMenu>{secondary}</MoreMenu> : secondary)}
                        {actions}
                    </div>
                )}
            </div>
            {children}
        </div>
    );
}

/** Longer helper text for a page, folded away under "About this page". */
export function AboutThisPage({ children, className = '' }) {
    return (
        <details className={`mb-5 rounded-md border border-slate-200 bg-white/70 text-sm text-slate-600 ${className}`}>
            <summary className="cursor-pointer select-none px-3 py-2 font-medium text-slate-700 hover:text-slate-900">About this page</summary>
            <div className="px-3 pb-3 space-y-2">{children}</div>
        </details>
    );
}
